import React, { useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';

const AddProductSaleModal = ({
  newProductSale,
  setNewProductSale,
  onSubmit,
  onImageUpload,
  saleCategories = []
}) => {
  const [previewImages, setPreviewImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const editorRef = useRef(null);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    
    setPreviewImages([...previewImages, ...previews]);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const handleImageDelete = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      // Remove preview image
      const updatedPreviews = previewImages.filter((_, i) => i !== index);
      const updatedFiles = selectedFiles.filter((_, i) => i !== index);
      setPreviewImages(updatedPreviews);
      setSelectedFiles(updatedFiles);
      
      // Cleanup preview URL
      URL.revokeObjectURL(previewImages[index].url);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productSaleData = { 
      ...newProductSale,
      files: selectedFiles,
      description: editorRef.current ? editorRef.current.getContent() : ''
    };
    
    try {
      await onSubmit(e, productSaleData);
      // Reset image previews and selected files after successful submission
      previewImages.forEach(preview => URL.revokeObjectURL(preview.url));
      setPreviewImages([]);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error submitting product sale:', error);
    }
  };

  return (
    <div className="modal" id="addProductSaleModal" tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">Ajouter un produit en vente</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              {/* Image Upload Section */}
              <div className="mb-4">
                <label className="form-label">Images du produit <span className="text-danger">*</span></label>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  {/* Preview Images */}
                  {previewImages.map((preview, index) => (
                    <div key={index} className="position-relative" style={{ width: '150px', height: '150px' }}>
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        className="img-thumbnail"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                        onClick={() => handleImageDelete(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                
                <div className="mb-3">
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    required={previewImages.length === 0}
                  />
                  <small className="text-muted">
                    Vous pouvez sélectionner plusieurs images à la fois. Au moins une image est requise.
                  </small>
                </div>
              </div>

              {/* Product Details */}
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Titre <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProductSale.title}
                      onChange={(e) => setNewProductSale({...newProductSale, title: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Code EAN</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProductSale.ean_code}
                      onChange={(e) => setNewProductSale({...newProductSale, ean_code: e.target.value})}
                      placeholder="Code-barres"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Description <span className="text-danger">*</span></label>
                <Editor
                  onInit={(evt, editor) => editorRef.current = editor}
                  init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                      'lists',
                      'autolink',
                      'autoresize',
                      'help',
                      'wordcount'
                    ],
                    toolbar: 'bold italic underline | ' +
                            'bullist numlist | ' +
                            'removeformat help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                  }}
                  onEditorChange={(content) => {
                    setNewProductSale(prev => ({...prev, description: content}));
                  }}
                />
              </div>

              {/* Pricing and Stock */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Prix unitaire (€) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={newProductSale.unit_price}
                      onChange={(e) => setNewProductSale({...newProductSale, unit_price: parseFloat(e.target.value)})}
                      required
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Quantité groupée</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newProductSale.bulk_quantity}
                      onChange={(e) => setNewProductSale({...newProductSale, bulk_quantity: parseInt(e.target.value)})}
                      min="2"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Prix groupé (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={newProductSale.bulk_price}
                      onChange={(e) => setNewProductSale({...newProductSale, bulk_price: parseFloat(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Stock</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newProductSale.stock_quantity}
                      onChange={(e) => setNewProductSale({...newProductSale, stock_quantity: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Catégorie</label>
                    <select
                      className="form-select"
                      value={newProductSale.sale_category ?? ''}
                      onChange={(e) => setNewProductSale({
                        ...newProductSale,
                        sale_category: e.target.value ? parseInt(e.target.value, 10) : null
                      })}
                    >
                      <option value="">— Aucune —</option>
                      {saleCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Marque</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProductSale.brand}
                      onChange={(e) => setNewProductSale({...newProductSale, brand: e.target.value})}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Tranche d'âge</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProductSale.age_range}
                      onChange={(e) => setNewProductSale({...newProductSale, age_range: e.target.value})}
                      placeholder="ex: 0-6 mois"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Poids (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={newProductSale.weight}
                      onChange={(e) => setNewProductSale({...newProductSale, weight: parseFloat(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Statut</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newProductSale.is_active}
                        onChange={(e) => setNewProductSale({...newProductSale, is_active: e.target.checked})}
                      />
                      <label className="form-check-label">
                        {newProductSale.is_active ? 'Actif' : 'Inactif'}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Produit vedette</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newProductSale.featured}
                        onChange={(e) => setNewProductSale({...newProductSale, featured: e.target.checked})}
                      />
                      <label className="form-check-label">
                        {newProductSale.featured ? 'Oui' : 'Non'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {newProductSale.is_private && (
                <div className="alert alert-info py-2 px-3 small">
                  <strong>Ce produit reste visible par tout le monde.</strong> Les visiteurs anonymes et les nouveaux clients voient le prix unitaire normal.
                  <br />Les clients <strong>déjà référencés</strong> (ayant passé au moins une commande payée) voient en plus le tarif vente privée saisi ci-dessous et paient à ce tarif.
                </div>
              )}

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Tarif vente privée</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newProductSale.is_private}
                        onChange={(e) => setNewProductSale({...newProductSale, is_private: e.target.checked})}
                      />
                      <label className="form-check-label">
                        {newProductSale.is_private ? 'Activé pour les clients référencés' : 'Désactivé'}
                      </label>
                    </div>
                  </div>
                </div>
                {newProductSale.is_private && (
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Prix vente privée (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        placeholder="(vide = prix unitaire)"
                        value={newProductSale.private_price ?? ''}
                        onChange={(e) => setNewProductSale({
                          ...newProductSale,
                          private_price: e.target.value === '' ? null : parseFloat(e.target.value)
                        })}
                      />
                      <small className="text-muted">Laisser vide pour appliquer le prix unitaire.</small>
                    </div>
                  </div>
                )}
              </div>

              {/* SEO Section */}
              {/* <div className="mt-4">
                <h6 className="border-bottom pb-2">SEO (Optionnel)</h6>
                <div className="mb-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newProductSale.meta_title}
                    onChange={(e) => setNewProductSale({...newProductSale, meta_title: e.target.value})}
                    maxLength="60"
                  />
                  <small className="text-muted">Recommandé : 50-60 caractères</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={newProductSale.meta_description}
                    onChange={(e) => setNewProductSale({...newProductSale, meta_description: e.target.value})}
                    maxLength="160"
                  />
                  <small className="text-muted">Recommandé : 150-160 caractères</small>
                </div>
              </div> */}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                Ajouter
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductSaleModal;